import { useCallback } from 'react';
import {
  ReportRecord,
  setDangerIssues,
  setIncorrectTimeNotes,
  setReportData,
  setReportError,
  setReportLoading,
} from '../redux/productSlice';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { whiteListUsers } from '../utils/whiteListUsers';
import {
  IncorrectTimeNote,
  Issue,
  MAX_PAGE_SIZE,
  RecordFunc,
  ReportParams,
} from '../utils/model';
import { getProjectIssuesGql } from '../gitlabApi/gqlApi/getProjectIssues';
import { getGroupIssuesGql } from '../gitlabApi/gqlApi/getGroupIssues';

export const useClockReportGenerate = (): RecordFunc => {
  const dispatch = useAppDispatch();
  const username = useAppSelector((s) => s.user.user?.username?.toLowerCase());
  const wl = whiteListUsers.indexOf(username ?? '') >= 0;

  const action = useCallback(
    async ({ from, to, product, projects, validateNotes }: ReportParams) => {
      dispatch(setReportError(undefined));
      dispatch(setDangerIssues([]));
      dispatch(setIncorrectTimeNotes([]));
      dispatch(setReportData([]));
      try {
        let newData: ReportRecord[] = [];
        const dangerIssues: Issue[] = [];
        const incorrectTimeNotes: IncorrectTimeNote[] = [];
        const addIncorrectTimeNote = (incorrectTimeNote: IncorrectTimeNote) => {
          incorrectTimeNotes.push(incorrectTimeNote);
        };
        const processIssues = async (issues: Issue[]) => {
          let i = 0;
          let danger = '';
          for (const issue of issues) {
            if (
              (issue.timeLogsFullLength ?? 0) >= MAX_PAGE_SIZE ||
              (issue.notesFullLength ?? 0) >= MAX_PAGE_SIZE
            ) {
              dangerIssues.push(issue);
            }
            dispatch(setReportLoading(`${issue.project.name}: ${i++}/${issues.length}`));
            const timeLogs = issue.timeLogs.filter(
              (r) =>
                r.spentAt.getTime() >= from.getTime() &&
                r.spentAt.getTime() <= to.getTime(),
            );
            for (const timeLog of timeLogs) {
              if (!wl && timeLog.user.username.toLowerCase() !== username) continue;
              const target = newData.find(
                (r) => r.author.username === timeLog.user.username,
              );
              if (target) {
                target.total += timeLog.timeSpent;
                target.records.push(timeLog);
              } else {
                let res: ReportRecord = {
                  author: timeLog.user,
                  records: [timeLog],
                  total: timeLog.timeSpent,
                };
                newData.push(res);
              }
            }
          }
          dispatch(setReportData(newData));
          if (danger !== '') {
            dispatch(
              setReportError(`Possible incorrect report. Too many records. See${danger}`),
            );
          }
          return danger !== '';
        };
        let dangerAny = false;
        for (const project of product.directProjects) {
          dispatch(setReportLoading(project.name));
          const setProgress = (progress: string) =>
            dispatch(setReportLoading(`${project.name}: ${progress}`));
          const issues = await getProjectIssuesGql({
            project,
            from,
            to,
            setProgress,
            projects,
            addIncorrectTimeNote,
            validateNotes,
          });
          const dangerIssues = await processIssues(issues);
          dangerAny = dangerIssues || dangerAny;
        }
        for (const group of product.groupPaths) {
          dispatch(setReportLoading(group));
          const setProgress = (progress: string) =>
            dispatch(setReportLoading(`${group}: ${progress}`));
          const issues = await getGroupIssuesGql({
            projects: product.projects,
            addIncorrectTimeNote,
            groupPath: group,
            setProgress,
            from,
            to,
            validateNotes,
          });
          const dangerIssues = await processIssues(issues);
          dangerAny = dangerIssues || dangerAny;
        }
        dispatch(setDangerIssues(dangerIssues));
        dispatch(setIncorrectTimeNotes(incorrectTimeNotes));
        if (newData.length === 0 && !dangerAny) {
          dispatch(setReportError('There are no clock records!'));
        }
      } catch (err: any) {
        dispatch(setReportError(err.message));
      } finally {
        dispatch(setReportLoading(undefined));
      }
    },
    [dispatch, username, wl],
  );
  return action;
};
